import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BINDABLE_ACTIONS,
  loadSet2Bindings,
  saveSet2Bindings,
  resetSet2Bindings,
  codeToLabel,
} from '../utils/keybindings';
import { fetchSettings, pushSettings } from '../services/api';
import KeyboardLayout, { GROUP_COLORS } from './KeyboardLayout';

// ── Set 1 shortcuts (sola lettura) ───────────────────────────────────────────
const SET1_SHORTCUTS = [
  { group: 'Atleta', items: [
    { label: 'Atleta Blu',              keys: 'B' },
    { label: 'Atleta Rosso',            keys: 'R' },
  ]},
  { group: 'Tipo azione', items: [
    { label: 'Attacco',                 keys: 'A' },
    { label: 'Difesa',                  keys: 'D' },
  ]},
  { group: 'Preset', items: [
    { label: 'Pugno corpo punto',       keys: '1' },
    { label: 'Corpo punto',             keys: '2' },
    { label: 'Testa punto',             keys: '3' },
    { label: 'Corpo rotazione punto',   keys: '4' },
    { label: 'Testa rotazione punto',   keys: '6' },
  ]},
  { group: 'Biomeccanica', items: [
    { label: 'Gamba sinistra',          keys: 'X → S' },
    { label: 'Gamba destra',            keys: 'X → D' },
    { label: 'Guardia chiusa',          keys: 'G → C' },
    { label: 'Guardia aperta',          keys: 'G → A' },
    { label: 'Distanza clinch',         keys: 'C → L' },
  ]},
  { group: 'Comandi', items: [
    { label: 'Toggle sequenza',         keys: '+' },
    { label: 'Conferma',                keys: 'Enter' },
    { label: 'Annulla',                 keys: 'Escape' },
  ]},
];

const GROUP_ORDER = ['Atleta', 'Tipo azione', 'Tecniche', 'Varianti', 'Preset', 'Esito', 'Bersaglio', 'Biomeccanica', 'Comandi', 'Video'];

// ── Abbreviazioni per la tastiera grafica ────────────────────────────────────
const ACTION_ABBR = {
  athlete_blue:       'BLU',
  athlete_red:        'RED',
  type_attack:        'ATT',
  type_defense:       'DIF',
  type_counter:       'CTR',
  type_counter_ant:   'Ca',
  type_counter_int:   'Ci',
  type_counter_2nd:   'C2',
  preset_1:           'P1',
  preset_2:           'P2',
  preset_3:           'P3',
  preset_4:           'P4',
  preset_6:           'P6',
  outcome_point:      'PNT',
  outcome_touch:      'TCH',
  outcome_miss:       'MIS',
  target_body:        'CRP',
  target_head:        'TST',
  leg_left:           'L sx',
  leg_right:          'L dx',
  guard_closed:       'G ch',
  guard_open:         'G ap',
  distance_clinch:    'CLN',
  tech_bonus:         'ROT',
  tech_Bn:            'Bn',
  tech_Dl:            'Dl',
  tech_Nr:            'Nr',
  tech_Pk:            'Pk',
  tech_Dw:            'Dw',
  tech_Md:            'Md',
  tech_Pugno:         'Pug',
  tech_2Bn:           '2Bn',
  tech_360Bn:         '360B',
  tech_360Dl:         '360D',
  tech_Gr:            'Gr',
  tech_Clsh:          'Clsh',
  tech_Bna:           'Bna',
  tech_Dla:           'Dla',
  tech_Nra:           'Nra',
  tech_2Bnf:          '2Bnf',
  sequence_toggle:    'SEQ',
  confirm:            'OK',
  cancel:             'ESC',
  video_play_pause:   '▶⏸',
  video_seek_back:    '←1s',
  video_seek_fwd:     '→1s',
  video_frame_back:   '◀fr',
  video_frame_fwd:    'fr▶',
};

function groupActions(actions) {
  const groups = {};
  for (const action of actions) {
    if (!groups[action.group]) groups[action.group] = [];
    groups[action.group].push(action);
  }
  return groups;
}

function keyLabel(code) {
  if (!code) return '—';
  return codeToLabel(code);
}

// ── Componente principale ─────────────────────────────────────────────────────
function KeybindingsEditor({ onClose }) {
  const [activeTab, setActiveTab] = useState('set2');
  const [bindings, setBindings]   = useState(() => loadSet2Bindings());
  const [recording, setRecording] = useState(null);
  const [conflict, setConflict]   = useState(null);
  const [saved, setSaved]         = useState(false);
  const [kbLayout, setKbLayout]   = useState(() => {
    try { return localStorage.getItem('tkd_kb_layout') || 'ansi'; } catch { return 'ansi'; }
  });

  // Carica dal server al mount — sovrascrive localStorage se il server ha dati
  useEffect(() => {
    fetchSettings()
      .then(data => {
        if (data?.keybindings && typeof data.keybindings === 'object') {
          const merged = { ...loadSet2Bindings(), ...data.keybindings };
          setBindings(merged);
          saveSet2Bindings(merged); // aggiorna localStorage con i dati server
        }
      })
      .catch(() => {}); // se offline o errore, usa localStorage già caricato
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mappa azioni arricchita con abbreviazioni
  const actionMap = useMemo(() => {
    const m = new Map();
    for (const a of BINDABLE_ACTIONS) {
      m.set(a.id, { ...a, abbr: ACTION_ABBR[a.id] || a.label });
    }
    return m;
  }, []);

  // Listener tasto durante recording
  useEffect(() => {
    if (!recording) return;
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const code = e.code;
      // Ignora tasti di sistema e modifier-only
      const SKIP = new Set([
        'F5', 'F12', 'Tab',
        'ShiftLeft', 'ShiftRight',
        'ControlLeft', 'ControlRight',
        'AltLeft', 'AltRight',
        'MetaLeft', 'MetaRight',
        'CapsLock',
      ]);
      if (SKIP.has(code)) return;

      const existing = Object.entries(bindings).find(
        ([id, k]) => k === code && id !== recording
      );
      if (existing) {
        setConflict({ key: code, existingId: existing[0] });
        return;
      }

      setConflict(null);
      setBindings(prev => ({ ...prev, [recording]: code }));
      setRecording(null);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [recording, bindings]);

  // Escape per chiudere (solo quando non in recording)
  useEffect(() => {
    if (recording) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [recording, onClose]);

  const handleRowClick = useCallback((actionId) => {
    setConflict(null);
    setRecording(prev => prev === actionId ? null : actionId);
  }, []);

  const handleClear = useCallback((actionId, e) => {
    e.stopPropagation();
    setBindings(prev => ({ ...prev, [actionId]: '' }));
    if (recording === actionId) setRecording(null);
  }, [recording]);

  const handleSave = () => {
    saveSet2Bindings(bindings); // localStorage (accesso immediato al prossimo avvio)
    pushSettings({ keybindings: bindings }).catch(() => {}); // server (persistenza stabile)
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const handleReset = () => {
    setBindings(resetSet2Bindings());
    setRecording(null);
    setConflict(null);
  };

  const handleConflictReplace = () => {
    if (!conflict) return;
    setBindings(prev => ({
      ...prev,
      [conflict.existingId]: '',
      [recording]: conflict.key,
    }));
    setConflict(null);
    setRecording(null);
  };

  const handleLayoutChange = (layout) => {
    setKbLayout(layout);
    try { localStorage.setItem('tkd_kb_layout', layout); } catch {}
  };

  const handleExport = () => {
    const json = JSON.stringify(bindings, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `tkd-shortcuts-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input    = document.createElement('input');
    input.type     = 'file';
    input.accept   = '.json';
    input.onchange = (e) => {
      const file   = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target.result);
          if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error();
          setBindings(prev => ({ ...prev, ...parsed }));
          setConflict(null);
          setRecording(null);
        } catch {
          alert('File non valido. Usa un JSON esportato da questa pagina.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const groups = groupActions(BINDABLE_ACTIONS);
  const conflictLabel = conflict
    ? BINDABLE_ACTIONS.find(a => a.id === conflict.existingId)?.label
    : null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        width: 1200,
        maxWidth: '98vw',
        maxHeight: '94vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 60px rgba(0,0,0,0.7)',
      }}>

        {/* ── Header ────────────────────────────────────────────── */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-text)' }}>
              Keyboard Shortcuts
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 2 }}>
              Configurazione completa scorciatoie tastiera
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'var(--color-text-dim)',
              fontSize: 22, cursor: 'pointer', lineHeight: 1,
              padding: '4px 8px', borderRadius: 4,
            }}
          >✕</button>
        </div>

        {/* ── Tab bar ───────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          padding: '0 24px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}>
          {[
            { id: 'set1', label: 'Set 1 — Riferimento' },
            { id: 'set2', label: 'Set 2 — Configurabile' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setRecording(null); setConflict(null); }}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #e8516a' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--color-text)' : 'var(--color-text-dim)',
                padding: '10px 20px',
                fontSize: 13,
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Content ───────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 20px' }}>

          {/* SET 1 — sola lettura */}
          {activeTab === 'set1' && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 14 }}>
                Le scorciatoie del Set 1 sono fisse. Usa il Set 2 per personalizzare le shortcut a tasto singolo.
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 16,
              }}>
                {SET1_SHORTCUTS.map(group => {
                  const gc = GROUP_COLORS[group.group] || { bg: '#222', border: 'var(--bg-elevated)', text: '#888' };
                  return (
                    <div key={group.group}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.08em', color: gc.text, marginBottom: 6,
                      }}>
                        {group.group}
                      </div>
                      {group.items.map((item, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '5px 10px', borderRadius: 5,
                          background: 'rgba(255,255,255,0.02)',
                          marginBottom: 2,
                        }}>
                          <span style={{ fontSize: 12, color: 'var(--color-text-sub)' }}>{item.label}</span>
                          <kbd style={{
                            background: gc.bg,
                            color: gc.text,
                            border: `1px solid ${gc.border}`,
                            borderRadius: 4, padding: '2px 8px',
                            fontSize: 11, fontFamily: 'monospace',
                          }}>
                            {item.keys}
                          </kbd>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SET 2 — configurabile */}
          {activeTab === 'set2' && (
            <div>
              {/* Conflitto */}
              {conflict && (
                <div style={{
                  marginBottom: 12, padding: '10px 14px',
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 6, fontSize: 12, color: 'var(--color-red-light)',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span>
                    <kbd style={{
                      background: 'var(--bg-elevated)', padding: '1px 5px', borderRadius: 3,
                      fontFamily: 'monospace',
                    }}>
                      {keyLabel(conflict.key)}
                    </kbd>
                    {' '}è già assegnato a <strong>{conflictLabel}</strong>.
                  </span>
                  <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }} onClick={handleConflictReplace}>Sostituisci</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setConflict(null); setRecording(null); }}>Annulla</button>
                </div>
              )}

              <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 14 }}>
                Clicca su una riga, poi premi il tasto da assegnare. Ogni tasto può essere assegnato a una sola azione.
              </div>

              {/* Lista azioni per categoria */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 16,
                marginBottom: 24,
              }}>
                {GROUP_ORDER.map(groupName => {
                  const items = groups[groupName];
                  if (!items) return null;
                  const gc = GROUP_COLORS[groupName] || { bg: '#222', border: 'var(--bg-elevated)', text: '#888' };
                  return (
                    <div key={groupName}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.08em', color: gc.text, marginBottom: 6,
                      }}>
                        {groupName}
                      </div>
                      {items.map(action => {
                        const isRec = recording === action.id;
                        const key = bindings[action.id];
                        return (
                          <div
                            key={action.id}
                            onClick={() => handleRowClick(action.id)}
                            style={{
                              display: 'flex', alignItems: 'center',
                              padding: '6px 10px', borderRadius: 5,
                              marginBottom: 2, cursor: 'pointer',
                              background: isRec ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.02)',
                              border: `1px solid ${isRec ? 'var(--color-yellow)' : 'transparent'}`,
                              transition: 'background 0.1s',
                            }}
                          >
                            <span style={{
                              flex: 1, fontSize: 12,
                              color: isRec ? 'var(--color-yellow)' : 'var(--color-text-sub)',
                            }}>
                              {action.label}
                            </span>
                            {isRec ? (
                              <span style={{ fontSize: 11, color: 'var(--color-yellow)' }}>
                                premi un tasto…
                              </span>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <kbd style={{
                                  background: key ? gc.bg : 'var(--bg-card)',
                                  color: key ? gc.text : 'var(--color-text-dim)',
                                  border: `1px solid ${key ? gc.border : 'var(--color-border)'}`,
                                  borderRadius: 4, padding: '2px 8px',
                                  fontSize: 11, fontFamily: 'monospace',
                                  minWidth: 30, textAlign: 'center',
                                }}>
                                  {keyLabel(key)}
                                </kbd>
                                {key && (
                                  <button
                                    onClick={e => handleClear(action.id, e)}
                                    style={{
                                      background: 'none', border: 'none',
                                      color: 'var(--color-text-dim)', cursor: 'pointer',
                                      fontSize: 13, lineHeight: 1, padding: 2,
                                    }}
                                    title="Rimuovi"
                                  >✕</button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* ── Tastiera grafica ──────────────────────────────── */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 12,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-dim)' }}>
                    Anteprima tastiera
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[
                      { id: 'ansi',     label: 'ANSI (US)' },
                      { id: 'iso',      label: 'ISO (EU)' },
                      { id: 'tkl-ansi', label: 'TKL ANSI (K870T)' },
                      { id: 'tkl-iso',  label: 'TKL ISO' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => handleLayoutChange(opt.id)}
                        style={{
                          background: kbLayout === opt.id ? 'rgba(232,81,106,0.15)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${kbLayout === opt.id ? 'rgba(232,81,106,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          color: kbLayout === opt.id ? 'var(--accent)' : 'var(--color-text-dim)',
                          borderRadius: 4, padding: '3px 10px',
                          fontSize: 11, cursor: 'pointer',
                          fontWeight: kbLayout === opt.id ? 600 : 400,
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Legenda colori */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  {GROUP_ORDER.map(g => {
                    const gc = GROUP_COLORS[g];
                    if (!gc) return null;
                    return (
                      <div key={g} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{
                          width: 10, height: 10, borderRadius: 2,
                          background: gc.bg, border: `1px solid ${gc.border}`,
                        }} />
                        <span style={{ fontSize: 9, color: gc.text }}>{g}</span>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
                  <KeyboardLayout
                    bindings={bindings}
                    actionMap={actionMap}
                    layout={kbLayout}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer (solo Set 2) ────────────────────────────────── */}
        {activeTab === 'set2' && (
          <div style={{
            padding: '12px 24px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', gap: 8, alignItems: 'center',
            flexShrink: 0,
          }}>
            <button className="btn btn-ghost" onClick={handleExport} title="Salva shortcut su file JSON">
              Esporta
            </button>
            <button className="btn btn-ghost" onClick={handleImport} title="Carica shortcut da file JSON">
              Importa
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost" onClick={handleReset}>
              Ripristina default
            </button>
            <button
              className="btn"
              style={{ background: saved ? 'var(--color-green)' : 'var(--accent)', color: '#fff', fontWeight: 600, transition: 'background 0.2s' }}
              onClick={handleSave}
            >
              {saved ? '✓ Salvato' : 'Salva'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default KeybindingsEditor;
